import React from "react";
import {
  Badge,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuGroup,
  MenuDivider,
  Icon,
  Box,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { useTranslation } from "react-i18next";

const marketplacesEU = [
  { code: "fr", label: "France", flag: "🇫🇷" },
  { code: "de", label: "Germany", flag: "🇩🇪" },
  { code: "en-GB", label: "United Kingdom", flag: "🇬🇧" },
  { code: "it", label: "Italy", flag: "🇮🇹" },
  { code: "es", label: "Spain", flag: "🇪🇸" },
  { code: "nl", label: "Netherlands", flag: "🇳🇱" },
  { code: "pl", label: "Poland", flag: "🇵🇱" },
  { code: "sv", label: "Sweden", flag: "🇸🇪" },
  { code: "tr", label: "Turkey", flag: "🇹🇷" },
  { code: "be", label: "Belgium", flag: "🇧🇪" },
  { code: "ie", label: "Ireland", flag: "🇮🇪" },
  { code: "ar", label: "UAE", flag: "🇦🇪" },
  { code: "ar-SA", label: "Saudi Arabia", flag: "🇸🇦" },
  { code: "ar-EG", label: "Egypt", flag: "🇪🇬" },
];

const marketplacesUS = [
  { code: "en", label: "United States", flag: "🇺🇸" },
  { code: "en-CA", label: "Canada", flag: "🇨🇦" },
  { code: "es-MX", label: "Mexico", flag: "🇲🇽" },
  { code: "pt", label: "Brazil", flag: "🇧🇷" },
];

export const marketplaces = [...marketplacesEU, ...marketplacesUS];

interface MarketplaceSelectorProps {
  currentLanguage: string;
  onLanguageChange?: (lang: string) => void;
  availableLanguages?: string[];
  isReadOnly?: boolean;
}

const MarketplaceSelector = ({ 
  currentLanguage, 
  onLanguageChange,
  availableLanguages,
  isReadOnly = false
}: MarketplaceSelectorProps) => {
  const { t } = useTranslation();

  const currentMarketplace = marketplaces.find(
    (m) => m.code === currentLanguage,
  ) || { code: currentLanguage, label: currentLanguage, flag: "🏳️" };

  if (isReadOnly || !onLanguageChange) {
      return (
        <Badge colorScheme="white" fontSize="md" px={2} py={1} borderRadius="md">
            {currentMarketplace?.flag} {currentMarketplace?.code.toUpperCase()}
        </Badge>
      );
  }

  return (
    <Box textAlign="left">
      <Menu>
        <MenuButton
          as={Button}
          rightIcon={<ChevronDownIcon />}
          size="sm"
        >
          <Badge colorScheme="white" bg="transparent" p={0}>
            {currentMarketplace?.flag} {currentMarketplace?.code.toUpperCase()}
          </Badge>
        </MenuButton>
        <MenuList zIndex={100} maxH="70vh" overflowY="auto">
          <MenuGroup title={t("realmEU")}>
            {marketplacesEU.map((marketplace) => {
              const isAvailable = availableLanguages ? availableLanguages.includes(marketplace.code) : true;
              return (
                <MenuItem
                  key={marketplace.code}
                  onClick={() => onLanguageChange(marketplace.code)}
                >
                  <Icon mr={2} as="span">{marketplace.flag}</Icon>
                  {t(marketplace.label)}
                  {!isAvailable && (
                    <Badge ml={2} colorScheme="gray" fontSize="xs">
                      {t("toTranslate")}
                    </Badge>
                  )}
                </MenuItem>
              );
            })}
          </MenuGroup>
          <MenuDivider />
          <MenuGroup title={t("realmUS")}>
            {marketplacesUS.map((marketplace) => {
              const isAvailable = availableLanguages ? availableLanguages.includes(marketplace.code) : true;
              return (
                <MenuItem
                  key={marketplace.code}
                  onClick={() => onLanguageChange(marketplace.code)}
                >
                  <Icon mr={2} as="span">{marketplace.flag}</Icon>
                  {t(marketplace.label)}
                  {!isAvailable && (
                    <Badge ml={2} colorScheme="gray" fontSize="xs">
                      {t("toTranslate")}
                    </Badge>
                  )}
                </MenuItem>
              );
            })}
          </MenuGroup>
        </MenuList>
      </Menu>
    </Box>
  );
};

export default MarketplaceSelector;
